import axios from "axios";
import { useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";

import BASE_URL from "../constants/baseURL";
import { alert } from "../redux/slices/alertSlice";
import { apiError } from "../redux/slices/loaderSlice";
import { deleteAccessToken } from "../redux/slices/memberSlice";

export default function useAxios({
  baseURL = "adn_api",
  customBaseURL = "",
  customAuthToken = "",
  disableAuthToken = false,
  headers = "json",
} = {}) {
  const dispatch = useDispatch();
  const axiosInstance = useRef(axios.create());

  const token = sessionStorage.getItem("authToken");
  const ida_sso_login = sessionStorage.getItem("ida_sso_login");

  // const ssoToken = queryString.parse(window.location.search)?.token;
  const { authToken } = useSelector((state) => state.memberDetails);

  useEffect(() => {
    axiosInstance.current = axios.create({
      headers: {
        ...(!disableAuthToken &&
          ida_sso_login === null && {
            Authorization: customAuthToken || authToken || token,
          }),

        ...(ida_sso_login === "true" && {
          "x-sso-token": customAuthToken || authToken || token,
        }),

        ...(headers === "json" && {
          "content-type": "application/json",
        }),

        ...(headers === "formData" && {
          "Content-Type": "multipart/form-data",
        }),
      },

      ...(Boolean(customBaseURL)
        ? { baseURL: customBaseURL }
        : {
            //baseURL: process.env.REACT_APP_BASE_URL,
            baseURL: BASE_URL[baseURL].url + BASE_URL[baseURL].version,
          }),

      validateStatus: (status) => status < 500,
    });
  }, [
    headers,
    ida_sso_login,
    authToken,
    baseURL,
    customAuthToken,
    customBaseURL,
    token,
    disableAuthToken,
  ]);

  return useCallback(
    ({
      url,
      method = "GET",
      data,
      params,
      disableRedirect = false,
      disableError = false,
    }) =>
      axiosInstance
        .current({
          url: url,
          method: method,
          data: data,
          params: params,
        })
        .then((response) => {
          let message = [];
          if (Array.isArray(response.data.error)) {
            response.data.error.map((error) => {
              message.push(error.msg);
              return message;
            });
          }

          // if (
          //   response.data.code === 100 ||
          //   response.data.code === 101 ||
          //   response.data.code === 401
          // ) {
          //   dispatch(apiError(response.data.error));
          // }

          if (response.data.detail?.code === "#401") {
            dispatch(
              alert({
                message:
                  "Could not validate your credentials. Please log in again",
                type: "error",
              })
            );
            return dispatch(deleteAccessToken());
          }

          if (
            response.status === 401 ||
            response.statusText === "Unauthorized"
          ) {
            dispatch(
              alert({
                message: response.data.error,
                type: "error",
              })
            );

            return dispatch(deleteAccessToken());
          }

          if (response.status > 201 || !response.data.status) {
            if (!disableError) {
              if (message.length > 0) {
                dispatch(
                  alert({
                    multiple: message,
                    type: "error",
                  })
                );
              } else {
                dispatch(
                  alert({
                    message: response.data?.error || "Error loading data!",
                    type: "error",
                  })
                );
              }
              return {
                status: false,
                message:
                  message.length > 0
                    ? message
                    : response.data.error || response.data.message,
                ...(response.data?.message?.validationMessage && {
                  validationMessage: response.data?.message?.validationMessage,
                }),
              };
            }

            if (!disableRedirect) {
              dispatch(apiError(response.data?.message?.traceID));

              throw new Error();
            } else {
              return {
                status: false,
                message:
                  message.length > 0
                    ? message
                    : response.data.error || response.data.message,
                ...(response.data?.message?.validationMessage && {
                  validationMessage: response.data?.message?.validationMessage,
                }),
              };
            }
          }

          return response.data;
        }),
    [dispatch]
  );
}
